import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import ShipmentTimeline from "../../components/Operatorcomp/ShipmentTimeline";


const OperatorDashboard = () => {
  const [shipments, setShipments] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const unsubscribeFns = [];

    const fetchShipments = async () => {
      const clientsSnap = await getDocs(collection(db, "Clients"));

      clientsSnap.forEach((clientDoc) => {
        const clientId = clientDoc.id;
        const shipCol = collection(db, "Clients", clientId, "shipments");

        const unsub = onSnapshot(shipCol, (snapshot) => {
          setShipments((prev) => {
            const filtered = prev.filter((s) => s.clientId !== clientId);
            const current = snapshot.docs.map((doc) => ({
              docId: doc.id,
              clientId,
              ...doc.data(),
            }));
            return [...filtered, ...current];
          });
        });

        unsubscribeFns.push(unsub);
      });
    };

    fetchShipments();

    return () => {
      unsubscribeFns.forEach((fn) => fn());
    };
  }, []);

  const updateStatus = async (shipment, label) => {
    try {
      const ref = collection(db, "Clients", shipment.clientId, "shipments");
      const q = query(ref, where("id", "==", shipment.id));
      const snap = await getDocs(q);

      if (snap.empty) {
        alert("🚫 Shipment not found.");
        return;
      }

      const docRef = snap.docs[0].ref;
      const data = snap.docs[0].data();

      if (!Array.isArray(data?.timeline)) {
        alert("⚠️ Timeline format is invalid or missing.");
        return;
      }

      const now = Timestamp.now();
      const updatedTimeline = data.timeline.map((step) =>
        step.label.toLowerCase() === label.toLowerCase() && !step.timestamp
          ? { ...step, timestamp: now }
          : step
      );

      await updateDoc(docRef, {
        status: label.toLowerCase(),
        timeline: updatedTimeline,
        updatedAt: now,
      });
    } catch (err) {
      console.error("updateStatus failed:", err);
      alert("❌ Failed to update status.");
    }
  };

  const handleDelete = async (shipment) => {
    if (!window.confirm("Are you sure you want to delete this shipment?")) return;

    try {
      const ref = collection(db, "Clients", shipment.clientId, "shipments");
      const q = query(ref, where("id", "==", shipment.id));
      const snap = await getDocs(q);

      if (snap.empty) {
        alert("🚫 Shipment not found.");
        return;
      }

      await deleteDoc(snap.docs[0].ref);
    } catch (err) {
      console.error("Delete error:", err);
      alert("❌ Failed to delete shipment.");
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp?.seconds) return "⏳ Pending";
    return new Date(timestamp.seconds * 1000).toLocaleString();
  };

  const filtered = shipments.filter((s) =>
    s.id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 p-4 sm:p-6 md:p-8">
      <h2 className="text-3xl font-bold text-brand-teal">🧑‍✈️ Operator Dashboard</h2>

      <input
        type="text"
        placeholder="Search container ID..."
        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-teal"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {filtered.map((s) => (
        <div
          key={s.docId}
          className="bg-white p-5 rounded-xl shadow border space-y-4 transition hover:shadow-lg"
        >
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="text-lg font-semibold text-gray-800">
              📦 {s.clientId} • {s.origin} → {s.destination}
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => updateStatus(s, "Confirmed")}
                className="bg-yellow-400 hover:bg-yellow-500 text-white text-sm px-3 py-1 rounded"
              >
                ✅ Confirm
              </button>
              <button
                onClick={() => updateStatus(s, "Booked")}
                className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 py-1 rounded"
              >
                📦 Book
              </button>
              <button
                onClick={() => updateStatus(s, "Arrived")}
                className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded"
              >
                🛳 Arrived
              </button>
              <button
                onClick={() => handleDelete(s)}
                className="bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-1 rounded"
              >
                🗑 Delete
              </button>
            </div>
          </div>

          <div className="flex gap-4 mt-2 flex-wrap">
            {Array.isArray(s.timeline) &&
              s.timeline.map((step, idx) => (
                <div
                  key={`${s.docId}-${idx}`}
                  className={`px-4 py-2 rounded-md shadow-sm text-sm ${
                    step.timestamp
                      ? "bg-green-100 text-green-700 border border-green-300"
                      : "bg-gray-100 text-gray-500 border border-gray-200"
                  }`}
                >
                  <div className="font-semibold">{step.label}</div>
                  <div className="text-xs">{formatTimestamp(step.timestamp)}</div>
                </div>
              ))}
          </div>

          <button
            onClick={() =>
              setExpanded((prev) => (prev === s.docId ? null : s.docId))
            }
            className="text-sm text-blue-600 underline"
          >
            {expanded === s.docId ? "Hide Timeline" : "View Timeline"}
          </button>

          {expanded === s.docId && (
            <div className="mt-4">
              <ShipmentTimeline timeline={s.timeline} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default OperatorDashboard;
